import React, { useEffect, useState } from "react";
import { TablePagination, Modal, Box, FormControl, OutlinedInput, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAuthFetch from "../../hooks/useAuthFetch";
import CustomTable from "../table/CustomTable";
import CustomTableRow from "../table/CustomTableRow";
import TableHeader from "../table/TableHeader";
import MainTableBody from "../table/MainTableBody";
import { LoadingTableSkeleton } from "../../utils/common";
import NoDataTable from "../table/NoDataTable";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Add, CloseOutlined, DeleteOutline, EditOutlined } from "@mui/icons-material";
import toast from "react-hot-toast";
import useDebounce from "../../hooks/useDebounce";
import Cookies from "js-cookie";

import VCActionsModal from "./VCActionsModal";
import ViewDetialsModal from "./viewDetialsModal";
import {
  JOURNEY_STATUS_META,
  MONITORING_STATUSES,
  getStatusLabel,
} from "../../constants/journeyStatus";

/** One count per Monitoring card, defaulting to 0 for anything the API omits. */
const buildVolumetricData = (volumetrics = {}) =>
  MONITORING_STATUSES.reduce((acc, status) => {
    acc[status] = volumetrics[status] || 0;
    return acc;
  }, {});

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  padding: 4,
  borderRadius: 2,
};

const Monitoring = () => {
  const [openErr, setOpenErr] = useState(false);
  const customFetch = useAuthFetch();

  const [errMsg, setErrMsg] = useState(null);
  const userRole = JSON.parse(Cookies.get("userDets") || "{}").role;
  const [selectedRowCode, setSelectedRowCode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [metricsData, setMetricsData] = useState({
    "UDS-PDF": 0,
    "S3-PDF": 0,
    "S3-REC": 0,
    "SCREENSHOT": 0,
    "KYP-GEN": 0,
    "DIGI-UP": 0,
    "UDS-UP": 0,
    "MCA-UP": 0,
  });

  const [volumetricData, setVolumetricData] = useState(buildVolumetricData);


  const openUtility = (code) => {
    setSelectedRowCode(code);
    setIsModalOpen(true);
  };

  const fetchBacklogCounts = async (start = '', end = '') => {
    if ((start && !end) || (!start && end)) {
      setErrMsg("Both Start Date and End Date must be provided together.");
      setOpenErr(true);
      return;
    }
    try {
      const url = `${process.env.REACT_APP_API_URL}/v1/dashboard/application/getJourneyDashboardCountAPI`;
      const cleanStart = start && start.includes('T') ? start.split('T')[0] : start;
      const cleanEnd = end && end.includes('T') ? end.split('T')[0] : end;
      const body = {
        startTime: cleanStart ? `${cleanStart}T00:00:00.000Z` : "",
        endTime: cleanEnd ? `${cleanEnd}T23:59:59.999Z` : ""
      };
      console.log("Dashboard Payload Sent:", body);
      const { response, status } = await customFetch(url, "POST", body);
      if (status === 200 && response) {
        const { backlogs, volumetrics } = response;
        if (backlogs) {
          setMetricsData({
            "UDS-PDF": backlogs["PDF Not Uploaded to UDS"] || 0,
            "S3-PDF": backlogs["PDF Not Uploaded to S3"] || 0,
            "S3-REC": backlogs["Recording Not Uploaded to S3"] || 0,
            "SCREENSHOT": backlogs["Journey Screenshots Not Generated"] || 0,
            "KYP-GEN": backlogs["KYP Photo Not Generated"] || 0,
            "DIGI-UP": backlogs["KYP Digi Status is Not Updated"] || 0,
            "UDS-UP": backlogs["KYP UDS Status is Not Updated"] || 0,
            "MCA-UP": backlogs["KYP MCA Status is Not Updated"] || 0,
          });
        }
        if (volumetrics) {
          setVolumetricData(buildVolumetricData(volumetrics));
        }
      }

    } catch (error) {
      console.error("Failed to fetch dashboard metric indicators:", error);
    }
  };



  useEffect(() => {
    if (userRole === "ADMIN") {
      fetchBacklogCounts();
    }
  }, [userRole]);

  const onJourneyFilterSubmit = ({ start, end }) => {
    console.log("Journey API Filter Triggered:", { start, end });
  };

  const onBacklogFilterSubmit = ({ start, end }) => {
    fetchBacklogCounts(start, end);
  };

  const caseMetrics = [
    { name: "PDF Not Uploaded to UDS", code: "UDS-PDF", count: metricsData["UDS-PDF"], sheet: "Sheet1" },
    { name: "PDF Not Uploaded to S3", code: "S3-PDF", count: metricsData["S3-PDF"], sheet: "Sheet2" },
    { name: "Recording Not Uploaded to S3", code: "S3-REC", count: metricsData["S3-REC"], sheet: "-" },
    { name: "Journey Screenshots Not Generated", code: "SCREENSHOT", count: metricsData["SCREENSHOT"], sheet: "-" },
    { name: "KYP Photo Not Generated", code: "KYP-GEN", count: metricsData["KYP-GEN"], sheet: "Sheet3" },
    { name: "KYP Digi Status is Not Updated", code: "DIGI-UP", count: metricsData["DIGI-UP"], sheet: "Sheet4" },
    { name: "KYP UDS Status is Not Updated", code: "UDS-UP", count: metricsData["UDS-UP"], sheet: "Sheet5" },
    { name: "KYP MCA Status is Not Updated", code: "MCA-UP", count: metricsData["MCA-UP"], sheet: "Sheet6" },
  ];

  const journeyStatuses = MONITORING_STATUSES.map((status) => ({
    status,
    label: getStatusLabel(status),
    count: volumetricData[status],
    color: JOURNEY_STATUS_META[status].monitoringColor,
  }));

  const [journeyStart, setJourneyStart] = useState('');
  const [journeyEnd, setJourneyEnd] = useState('');
  const [backlogStart, setBacklogStart] = useState('');
  const [backlogEnd, setBacklogEnd] = useState('');

  return (
    <div className="py-1 px-4" style={{ height: `calc(100vh - 100px)` }}>
      <div className="flex flex-row mb-4 items-center justify-between">
        <p className="text-xl mb-0 font-extrabold">
          Monitoring Dashboard
        </p>
        <div className="flex gap-2 items-center">
        </div>
      </div>

      {userRole === "ADMIN" && (
        <div className="p-6 space-y-10 bg-gray-50 min-h-screen">
          <div>
            <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 p-5 border border-slate-200 rounded-xl bg-transparent">
              <div>
                <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                  Filter Dashboard Metrics
                </h2>
                <p className="text-xs text-slate-500 mt-1">Applies selected date metrics globally across Volumetrics and Backlogs</p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1.5 border border-slate-300 rounded-lg p-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all bg-transparent">
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider px-1">Start Date</span>
                  <input
                    type="date"
                    value={backlogStart}
                    onChange={(e) => setBacklogStart(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    className="bg-transparent text-xs font-medium text-slate-700 focus:outline-none cursor-pointer [color-scheme:light] before:content-[attr(placeholder)] before:text-slate-400 before:mr-2 before:hidden invalid:before:inline-block"
                    style={{
                      contentVisibility: 'auto'
                    }}
                  />
                </div>
                <div className="flex items-center gap-1.5 border border-slate-300 rounded-lg p-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all bg-transparent">
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider px-1">End Date</span>
                  <input
                    type="date"
                    value={backlogEnd}
                    onChange={(e) => setBacklogEnd(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    className="bg-transparent text-xs font-medium text-slate-700 focus:outline-none cursor-pointer [color-scheme:light] before:content-[attr(placeholder)] before:text-slate-400 before:mr-2 before:hidden invalid:before:inline-block"
                    style={{
                      contentVisibility: 'auto'
                    }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onBacklogFilterSubmit?.({ start: backlogStart, end: backlogEnd })}
                    className="bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-all shadow-sm"
                  >
                    Apply Filters
                  </button>
                  {(backlogStart || backlogEnd) && (
                    <button
                      onClick={() => {
                        setBacklogStart('');
                        setBacklogEnd('');
                        onBacklogFilterSubmit?.({ start: '', end: '' });
                      }}
                      className="border border-rose-200 hover:border-rose-300 text-xs font-semibold text-rose-600 hover:text-rose-700 px-3 py-2 rounded-lg transition-colors bg-transparent"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4 px-2">
                <h2 className="text-xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
                  Journey Volumetrics
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
                {journeyStatuses.map((item) => {
                  const colorSuffix = item.color ? item.color.replace('bg-', '') : 'slate-500';
                  const textColorClass = item.color ? `text-${colorSuffix}` : 'text-slate-500';
                  return (
                    <div
                      key={item.status}
                      className="bg-transparent border border-slate-200 p-4 rounded-xl flex justify-between items-center transition-all"
                    >
                      <div className="space-y-1 pr-2">
                        <div className={`text-xs font-bold uppercase tracking-tight break-words max-w-[180px] ${textColorClass}`}>
                          {item.label}
                        </div>
                        <div className="text-xl font-bold text-black">{item.count}</div>
                      </div>
                    </div>
                  );
                })}
                <div className="bg-transparent border border-slate-200 p-4 rounded-xl flex justify-between items-center transition-all">
                  <div className="space-y-1 pr-2">
                    <div className="text-xs font-bold uppercase tracking-tight break-words max-w-[180px] text-emerald-600">
                      TOTAL CASES
                    </div>
                    <div className="text-xl font-bold text-black">
                      {journeyStatuses.reduce((acc, curr) => acc + (curr.count || 0), 0).toLocaleString()}
                    </div>
                  </div>
                  <span className="text-[9px] font-extrabold px-2 py-1 rounded-md border uppercase whitespace-nowrap bg-transparent text-slate-500 border-slate-300">
                    Aggregate
                  </span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4 px-2">
                <h2 className="text-xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
                  Case Exceptions & Backlogs
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {caseMetrics.map((item) => (
                  <div
                    key={item.code}
                    className="bg-transparent border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-5 flex flex-col justify-between min-h-[200px]"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded border border-blue-100 uppercase tracking-widest">
                          {item.code}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-gray-900 mb-3 leading-snug">
                        {item.name}
                      </h3>
                    </div>
                    <div>
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Active Cases</div>
                        <div className={`text-3xl font-black ${item.count > 0 ? 'text-rose-600' : 'text-gray-400'}`}>
                          {item.count}
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
                        {userRole === "ADMIN" && (
                          <ViewDetialsModal
                            key={`${item.code}-view`}
                            defaultUtility={item.code}
                            setErrMsg={setErrMsg}
                            setOpenErr={setOpenErr}
                            isIconButton={true}
                            startDate={backlogStart}
                            endDate={backlogEnd}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default Monitoring;

